import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { KeyRound, Settings, Loader2, RefreshCw, Shield } from 'lucide-react';
import { settingsApi, ApiError } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { isTradeVerticalId, TRADE_VERTICAL_LABELS } from './verticalIds';

type BrokerRow = { label: string; configured?: boolean; connected?: boolean; detail?: string };

/**
 * Sprint 6 — per-vertical connection matrix + Settings entry (no secret entry here).
 */
export default function VerticalConnectionsTab() {
  const { verticalId } = useParams<{ verticalId: string }>();
  const vid = (verticalId || '').toLowerCase();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [broker, setBroker] = useState<Record<string, unknown> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const b = await settingsApi.brokerStatus();
      setBroker(b as Record<string, unknown>);
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!isTradeVerticalId(vid)) return null;

  const label = TRADE_VERTICAL_LABELS[vid];

  const rows: BrokerRow[] = [];
  if (vid === 'stocks') {
    rows.push({
      label: 'Alpaca (stocks)',
      configured: Boolean(broker?.alpaca_configured),
      connected: Boolean(broker?.alpaca_connected),
      detail: broker?.alpaca_mode ? String(broker.alpaca_mode) : undefined,
    });
  }

  const pill = (r: BrokerRow) => {
    if (r.configured && r.connected) return <Badge className="bg-emerald-600/15 text-emerald-700 dark:text-emerald-300">Ready</Badge>;
    if (r.configured) return <Badge variant="secondary">Configured</Badge>;
    return <Badge variant="outline">Not configured</Badge>;
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Refresh status</span>
        </Button>
      </div>
      {err ? <div className="text-sm text-destructive border border-destructive/30 rounded p-2">{err}</div> : null}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            Your connections — {label}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {loading && !broker ? (
            <div className="text-muted-foreground text-xs flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading broker status…
            </div>
          ) : null}
          <div className="rounded-md border divide-y">
            {rows.map((r) => (
              <div key={r.label} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
                <div>
                  <div className="font-medium text-foreground">{r.label}</div>
                  {r.detail ? <div className="text-xs text-muted-foreground mt-0.5">{r.detail}</div> : null}
                </div>
                {pill(r)}
              </div>
            ))}
          </div>
          <Link
            to="/settings"
            className="inline-flex items-center gap-2 text-primary font-medium text-sm underline-offset-4 hover:underline"
          >
            <Settings className="w-4 h-4" />
            Open Settings to add or validate keys
          </Link>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Platform-managed
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground space-y-2">
          <p>
            Shared market data, global AI keys, and org-wide news/index feeds are operated by an administrator — not
            overridden on this screen.
          </p>
          <p>
            Admins: see <strong>Admin → Integrations</strong> for a read-only snapshot of system flags and API health.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
